/**
 * スタンプラリー用 Zustand ストア
 *
 * スタンプの ON/OFF だけはレスポンスで手元の item を差し替える。
 * スポットの追加・削除はグループ構成が変わるため詳細を取り直す。
 */

import { create } from 'zustand'

import {
  createStamp,
  createStampRally,
  createStampRallyItem,
  deleteStamp,
  deleteStampRally,
  deleteStampRallyItem,
  getStampRallies,
  getStampRally,
  updateStampRally,
  updateStampRallyItem,
} from '@/api/services/stamp-rally'
import type {
  CreateItemParams,
  CreateRallyParams,
  StampRallyDetail,
  StampRallyItem,
  StampRallySummary,
  UpdateItemParams,
  UpdateRallyParams,
} from '@/api/types/stamp-rally'

type StampRallyState = {
  rallies: StampRallySummary[]
  isListLoading: boolean

  detail: StampRallyDetail | null
  isDetailLoading: boolean

  error: string | null

  fetchRallies: () => Promise<void>
  createRally: (params: CreateRallyParams) => Promise<StampRallySummary | null>
  updateRally: (rallyId: number, params: UpdateRallyParams) => Promise<void>
  removeRally: (rallyId: number) => Promise<void>

  fetchDetail: (rallyId: number) => Promise<void>
  addItem: (rallyId: number, params: CreateItemParams) => Promise<boolean>
  editItem: (rallyId: number, itemId: number, params: UpdateItemParams) => Promise<void>
  removeItem: (rallyId: number, itemId: number) => Promise<void>
  toggleStamp: (itemId: number, stamped: boolean) => Promise<void>

  clearDetail: () => void
  clearError: () => void
}

const toMessage = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback)

/** 差し替えた item を反映し、進捗も数え直す */
const replaceItem = (detail: StampRallyDetail | null, item: StampRallyItem): StampRallyDetail | null => {
  if (!detail) return null
  const groups = detail.groups.map((group) => ({
    ...group,
    items: group.items.map((current) => (current.id === item.id ? item : current)),
  }))
  const stampedCount = groups.reduce((sum, group) => sum + group.items.filter((i) => i.stamped_at).length, 0)
  return {
    ...detail,
    groups,
    stamped_count: stampedCount,
    progress: detail.total_count ? stampedCount / detail.total_count : 0,
  }
}

export const useStampRallyStore = create<StampRallyState>((set, get) => ({
  rallies: [],
  isListLoading: false,
  detail: null,
  isDetailLoading: false,
  error: null,

  fetchRallies: async () => {
    set({ isListLoading: true, error: null })
    try {
      set({ rallies: await getStampRallies() })
    } catch (error) {
      set({ error: toMessage(error, 'スタンプラリーの取得に失敗しました') })
    } finally {
      set({ isListLoading: false })
    }
  },

  createRally: async (params) => {
    try {
      const created = await createStampRally(params)
      set({ rallies: [created, ...get().rallies] })
      return created
    } catch (error) {
      set({ error: toMessage(error, 'スタンプラリーの作成に失敗しました') })
      return null
    }
  },

  updateRally: async (rallyId, params) => {
    try {
      const updated = await updateStampRally(rallyId, params)
      const { rallies, detail } = get()
      set({
        rallies: rallies.map((rally) => (rally.id === rallyId ? updated : rally)),
        detail: detail && detail.id === rallyId ? { ...detail, ...updated } : detail,
      })
    } catch (error) {
      set({ error: toMessage(error, 'スタンプラリーの更新に失敗しました') })
    }
  },

  removeRally: async (rallyId) => {
    try {
      await deleteStampRally(rallyId)
      set({ rallies: get().rallies.filter((rally) => rally.id !== rallyId) })
    } catch (error) {
      set({ error: toMessage(error, 'スタンプラリーの削除に失敗しました') })
    }
  },

  fetchDetail: async (rallyId) => {
    set({ isDetailLoading: true, error: null })
    try {
      set({ detail: await getStampRally(rallyId) })
    } catch (error) {
      set({ error: toMessage(error, 'スタンプラリーの取得に失敗しました') })
    } finally {
      set({ isDetailLoading: false })
    }
  },

  addItem: async (rallyId, params) => {
    try {
      await createStampRallyItem(rallyId, params)
      await get().fetchDetail(rallyId)
      return true
    } catch (error) {
      set({ error: toMessage(error, 'スポットの追加に失敗しました') })
      return false
    }
  },

  editItem: async (rallyId, itemId, params) => {
    try {
      await updateStampRallyItem(itemId, params)
      await get().fetchDetail(rallyId)
    } catch (error) {
      set({ error: toMessage(error, 'スポットの更新に失敗しました') })
    }
  },

  removeItem: async (rallyId, itemId) => {
    try {
      await deleteStampRallyItem(itemId)
      await get().fetchDetail(rallyId)
    } catch (error) {
      set({ error: toMessage(error, 'スポットの削除に失敗しました') })
    }
  },

  toggleStamp: async (itemId, stamped) => {
    try {
      const item = stamped ? await createStamp(itemId) : await deleteStamp(itemId)
      set({ detail: replaceItem(get().detail, item) })
    } catch (error) {
      set({ error: toMessage(error, 'スタンプの更新に失敗しました') })
    }
  },

  clearDetail: () => set({ detail: null }),
  clearError: () => set({ error: null }),
}))
